import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import client, { mensajeDeError } from '../../api/client';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Spinner from '../../components/ui/Spinner';
import { Campo, Etiqueta, Entrada, Selector, MensajeError } from '../../components/ui/Form';
import { sumarDias, aISO, fechaCompleta, horaLegible } from '../../utils/fechas';
import { TituloSeccion, BarraAcciones, TablaEnvoltura, Tabla, CeldaAcciones, SinDatos } from './comunes';

const DetalleClase = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
  margin-bottom: ${({ theme }) => theme.space.lg};

  h3 {
    font-size: 1.25rem;
    color: ${({ theme }) => theme.colors.sageDeep};
  }

  p {
    font-size: 0.92rem;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const Fila = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};
  flex-wrap: wrap;
`;

const AccionesDetalle = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.space.sm};
`;

const Filtros = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.xs};
`;

const FILTROS = [
  { valor: 'proximas', texto: 'Próximas' },
  { valor: 'pasadas', texto: 'Pasadas' },
  { valor: 'todas', texto: 'Todas' },
];

const DIAS_RANGO = 365;

// "Próximas"/"pasadas" se resuelven por fecha+hora exacta en el backend (soloFuturas/soloPasadas),
// no solo por fecha, para que una clase de hoy que ya pasó no aparezca como próxima (y viceversa).
function rangoDeFiltro(filtro) {
  const hoy = aISO(new Date());
  const desdeLejos = aISO(sumarDias(new Date(), -DIAS_RANGO));
  const hastaLejos = aISO(sumarDias(new Date(), DIAS_RANGO));
  if (filtro === 'proximas') return { desde: hoy, hasta: hastaLejos, soloFuturas: '1' };
  if (filtro === 'pasadas') return { desde: desdeLejos, hasta: hoy, soloPasadas: '1' };
  return { desde: desdeLejos, hasta: hastaLejos };
}

const vacio = { tipoClaseId: '', instructorId: '', fecha: '', horaInicio: '07:00', cupoMaximo: 8 };

function ClasesAdmin() {
  const [filtro, setFiltro] = useState('proximas');
  const [clases, setClases] = useState(null);
  const [tipos, setTipos] = useState([]);
  const [instructores, setInstructores] = useState([]);
  const [editando, setEditando] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    client.get('/tipos-clase').then(({ data }) => setTipos(data)).catch(() => {});
    client.get('/instructores').then(({ data }) => setInstructores(data)).catch(() => {});
  }, []);

  const cargar = useCallback(() => {
    client
      .get('/clases', { params: rangoDeFiltro(filtro) })
      .then(({ data }) => setClases(data))
      .catch(() => setClases([]));
  }, [filtro]);

  useEffect(() => {
    setClases(null);
    cargar();
  }, [cargar]);

  const abrir = (clase) => {
    setError('');
    setEditando(
      clase
        ? {
            id: clase.id,
            tipoClaseId: clase.tipo_clase_id,
            instructorId: clase.instructor_id,
            fecha: clase.fecha,
            horaInicio: clase.hora_inicio.slice(0, 5),
            cupoMaximo: clase.cupo_maximo,
          }
        : { ...vacio, fecha: aISO(new Date()) }
    );
  };

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setError('');
    const datos = { ...editando, horaInicio: `${editando.horaInicio}:00` };
    try {
      if (editando.id) {
        await client.put(`/clases/${editando.id}`, datos);
      } else {
        await client.post('/clases', datos);
      }
      setEditando(null);
      cargar();
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (id) => {
    setError('');
    try {
      await client.delete(`/clases/${id}`);
      cargar();
    } catch (err) {
      setError(mensajeDeError(err));
    }
  };

  const cambiar = (campo) => (e) => setEditando((d) => ({ ...d, [campo]: e.target.value }));
  return (
    <>
      <TituloSeccion>Clases y horarios</TituloSeccion>
      <BarraAcciones>
        <Filtros>
          {FILTROS.map(({ valor, texto }) => (
            <Button
              key={valor}
              $chico
              $variante={filtro === valor ? 'primario' : 'secundario'}
              onClick={() => setFiltro(valor)}
            >
              {texto}
            </Button>
          ))}
        </Filtros>
        <Button onClick={() => abrir(null)}>Nueva clase</Button>
      </BarraAcciones>
      {error && !editando && <MensajeError role="alert">{error}</MensajeError>}

      {!clases ? (
        <Spinner />
      ) : (
        <TablaEnvoltura>
          {clases.length === 0 ? (
            <SinDatos>No hay clases {filtro === 'pasadas' ? 'pasadas' : filtro === 'proximas' ? 'próximas' : ''} programadas.</SinDatos>
          ) : (
            <Tabla>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Hora</th>
                  <th>Clase</th>
                  <th>Instructor</th>
                  <th>Cupo</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {clases.map((c) => (
                  <tr key={c.id}>
                    <td>{fechaCompleta(c.fecha)}</td>
                    <td>{horaLegible(c.hora_inicio)}</td>
                    <td>{c.tipo_clase}</td>
                    <td>{c.instructor}</td>
                    <td>
                      <Badge $tono={c.cupo_disponible === 0 ? 'peligro' : 'exito'}>
                        {c.cupo_maximo - c.cupo_disponible}/{c.cupo_maximo}
                      </Badge>
                    </td>
                    <CeldaAcciones>
                      <Button $variante="fantasma" $chico onClick={() => setDetalle(c)}>
                        Ver
                      </Button>
                      <Button $variante="secundario" $chico onClick={() => abrir(c)}>
                        Editar
                      </Button>
                      <Button $variante="peligro" $chico onClick={() => eliminar(c.id)}>
                        Eliminar
                      </Button>
                    </CeldaAcciones>
                  </tr>
                ))}
              </tbody>
            </Tabla>
          )}
        </TablaEnvoltura>
      )}

      <Modal
        abierto={!!editando}
        titulo={editando?.id ? 'Editar clase' : 'Nueva clase'}
        onCerrar={() => setEditando(null)}
      >
        {editando && (
          <form onSubmit={guardar}>
            {error && <MensajeError role="alert">{error}</MensajeError>}
            <Campo>
              <Etiqueta htmlFor="c-tipo">Tipo de clase</Etiqueta>
              <Selector id="c-tipo" value={editando.tipoClaseId} onChange={cambiar('tipoClaseId')} required>
                <option value="">Selecciona…</option>
                {tipos.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nombre}
                  </option>
                ))}
              </Selector>
            </Campo>
            <Campo>
              <Etiqueta htmlFor="c-inst">Instructor</Etiqueta>
              <Selector id="c-inst" value={editando.instructorId} onChange={cambiar('instructorId')} required>
                <option value="">Selecciona…</option>
                {instructores.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.nombre}
                  </option>
                ))}
              </Selector>
            </Campo>
            <Campo>
              <Etiqueta htmlFor="c-fecha">Fecha</Etiqueta>
              <Entrada id="c-fecha" type="date" value={editando.fecha} onChange={cambiar('fecha')} required />
            </Campo>
            <Campo>
              <Etiqueta htmlFor="c-hora">Hora de inicio</Etiqueta>
              <Entrada id="c-hora" type="time" value={editando.horaInicio} onChange={cambiar('horaInicio')} required />
            </Campo>
            <Campo>
              <Etiqueta htmlFor="c-cupo">Cupo máximo</Etiqueta>
              <Entrada
                id="c-cupo"
                type="number"
                min="1"
                max="30"
                value={editando.cupoMaximo}
                onChange={cambiar('cupoMaximo')}
                required
              />
            </Campo>
            <Button type="submit" $completo disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </Button>
          </form>
        )}
      </Modal>

      <Modal abierto={!!detalle} titulo="Detalle de la clase" onCerrar={() => setDetalle(null)}>
        {detalle && (
          <>
            <DetalleClase>
              <h3>{detalle.tipo_clase}</h3>
              <p>
                {fechaCompleta(detalle.fecha)} · {horaLegible(detalle.hora_inicio)} ·{' '}
                {detalle.duracion_minutos} min
              </p>
              <Fila>
                <Badge $tono="arena">{detalle.instructor}</Badge>
                {detalle.nivel && <Badge $tono="arena">{detalle.nivel}</Badge>}
                <Badge $tono={detalle.cupo_disponible === 0 ? 'peligro' : 'exito'}>
                  {detalle.cupo_maximo - detalle.cupo_disponible}/{detalle.cupo_maximo} lugares ocupados
                </Badge>
              </Fila>
            </DetalleClase>
            <AccionesDetalle>
              <Button $variante="fantasma" onClick={() => setDetalle(null)}>
                Cerrar
              </Button>
              <Button as={Link} to={`/admin/checkin/${detalle.id}`} onClick={() => setDetalle(null)}>
                Ir a check-in de esta clase
              </Button>
            </AccionesDetalle>
          </>
        )}
      </Modal>
    </>
  );
}

export default ClasesAdmin;
